import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Button, Form,
  InputGroup, Badge, Alert, Modal, Spinner
} from 'react-bootstrap';
import {
  Shirt, Plus, Search, RefreshCw, Filter, X, Scissors, ArrowLeft 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import modelService from '../../../services/modelService';
import ModelCard from './ModelCard';
import CreateModelModal from './CreateModelModal';
import ModelDetailModal from './ModelDetailModal'; // ✅ NOUVEAU
import { useAuth } from '../../../contexts/AuthContext'; 
import { useBoutique } from '../../../contexts/BoutiqueContext'; 

const ModelsManagement = () => {
  const { user } = useAuth();
  const { isAtelierEnabled } = useBoutique();
  const isManager = user?.role === 'MANAGER';
  const navigate = useNavigate();

  // États
  const [models, setModels] = useState([]);
  const [filteredModels, setFilteredModels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Filtres
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ✅ NOUVEAU: état pour le modal de détail
  const [viewingModel, setViewingModel] = useState(null);

  // Chargement initial
  useEffect(() => {
    if (isAtelierEnabled) {
      loadModels();
      loadCategories();
    }
  }, [isAtelierEnabled]);

  // Filtrage en temps réel
  useEffect(() => {
    applyFilters();
  }, [searchQuery, selectedCategory, models]);

  const loadModels = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await modelService.getModels();

      if (response.success) {
        setModels(response.data || []);
      } else {
        setError(response.message || 'Erreur lors du chargement');
      }

    } catch (err) {
      console.error('❌ Erreur chargement modèles:', err);
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await modelService.getCategories();
      if (response.success) {
        setCategories(response.data || []);
      }
    } catch (err) {
      console.error('Erreur catégories:', err);
    }
  };

  const applyFilters = () => {
    let result = [...models];

    // Filtre par catégorie
    if (selectedCategory !== 'all') {
      result = result.filter(m =>
        m.category?.toLowerCase() === selectedCategory.toLowerCase()
      );
    }

    // Filtre par recherche
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(m =>
        m.name.toLowerCase().includes(q) ||
        (m.description && m.description.toLowerCase().includes(q))
      );
    }

    setFilteredModels(result);
  };

  const handleCreate = () => {
    setEditingModel(null);
    setShowCreateModal(true);
  };

  const handleEdit = (model) => {
    setEditingModel(model);
    setShowCreateModal(true);
  };

  // ✅ MODIFIÉ: ouvre maintenant le modal de détail au lieu d'éditer directement
  const handleView = (model) => {
    setViewingModel(model);
  };

  const handleModalSuccess = (savedModel) => {
    // Recharger pour avoir la liste à jour
    loadModels();
    loadCategories();
    setSuccess(
      editingModel
        ? `Modèle "${savedModel.name}" mis à jour avec succès`
        : `Modèle "${savedModel.name}" créé avec succès`
    );
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleDelete = (model) => {
    setDeleteConfirm(model);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      setDeleting(true);
      const response = await modelService.deleteModel(deleteConfirm.id);

      if (response.success) {
        setSuccess(`Modèle "${deleteConfirm.name}" supprimé`);
        setTimeout(() => setSuccess(null), 3000);
        loadModels();
        loadCategories();
      } else {
        setError(response.message || 'Erreur lors de la suppression');
      }

    } catch (err) {
      setError('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  const resetFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
  };

  // Si atelier désactivé, afficher un message
  if (!isAtelierEnabled) {
    return (
      <Container fluid className="py-4">
        <Card className="text-center py-5">
          <Card.Body>
            <Scissors size={64} className="text-muted mb-3" />
            <h4>Module Atelier non activé</h4>
            <p className="text-muted">
              Le module Atelier n'est pas activé pour cette boutique.
              Contactez votre administrateur.
            </p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* En-tête */}
      <Row className="mb-4">
        {/* ✅ Bouton retour au dashboard atelier */}
<Button
  variant="link"
  onClick={() => navigate('/atelier')}
  className="p-0 mb-2 text-decoration-none d-flex align-items-center gap-1"
>
  <ArrowLeft size={16} />
  <small>Retour au tableau de bord Atelier</small>
</Button>
        <Col>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h1 className="h3 mb-1 d-flex align-items-center gap-2">
                <Shirt size={28} className="text-primary" />
                Catalogue de Modèles
              </h1>
              <p className="text-muted mb-0">
                Gérez les modèles réutilisables de votre atelier
              </p>
            </div>

            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                onClick={loadModels}
                disabled={loading}
              >
                <RefreshCw size={16} className={loading ? 'spin' : ''} />
              </Button>

              {isManager && (
                <Button variant="primary" onClick={handleCreate}>
                  <Plus size={16} className="me-1" />
                  Nouveau modèle
                </Button>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* Alertes */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filtres */}
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={6}>
              <Form.Label className="small text-muted">Rechercher</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <Search size={16} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Nom ou description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setSearchQuery('')}
                  >
                    <X size={16} />
                  </Button>
                )}
              </InputGroup>
            </Col>

            <Col md={4}>
              <Form.Label className="small text-muted">Catégorie</Form.Label>
              <Form.Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="all">Toutes les catégories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </Form.Select>
            </Col>

            <Col md={2}>
              <Button
                variant="outline-secondary"
                className="w-100"
                onClick={resetFilters}
              >
                Réinitialiser
              </Button>
            </Col>
          </Row>

          <div className="mt-3 d-flex justify-content-between align-items-center">
            <div className="text-muted small">
              <strong>{filteredModels.length}</strong> modèle{filteredModels.length > 1 ? 's' : ''} affiché{filteredModels.length > 1 ? 's' : ''}
              {filteredModels.length !== models.length && (
                <span> sur <strong>{models.length}</strong></span>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Contenu */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <div className="mt-2 text-muted">Chargement des modèles...</div>
        </div>
      ) : filteredModels.length === 0 ? (
        <Card className="text-center py-5 shadow-sm">
          <Card.Body>
            <Shirt size={64} className="text-muted mb-3" />
            <h5>Aucun modèle trouvé</h5>
            <p className="text-muted mb-3">
              {searchQuery || selectedCategory !== 'all'
                ? 'Aucun modèle ne correspond à vos critères.'
                : 'Commencez par créer votre premier modèle.'}
            </p>
            {isManager && !searchQuery && selectedCategory === 'all' && (
              <Button variant="primary" onClick={handleCreate}>
                <Plus size={16} className="me-1" />
                Créer un modèle
              </Button>
            )}
          </Card.Body>
        </Card>
      ) : (
        <Row>
          {filteredModels.map(model => (
            <Col 
              key={model.id} 
              xl={3} lg={4} md={6} sm={12}
              className="mb-3"
            >
              <ModelCard
                model={model}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onView={handleView}
                isManager={isManager}
              />
            </Col>
          ))}
        </Row>
      )}

      {/* Modal création/édition */}
      <CreateModelModal
        show={showCreateModal}
        onHide={() => {
          setShowCreateModal(false);
          setEditingModel(null);
        }}
        onSuccess={handleModalSuccess}
        editingModel={editingModel}
        existingCategories={categories}
      />

      {/* ✅ NOUVEAU: Modal de détail */}
      <ModelDetailModal
        show={!!viewingModel}
        onHide={() => setViewingModel(null)}
        model={viewingModel}
        onEdit={handleEdit}
        isManager={isManager}
      />

      {/* Modal de confirmation de suppression */}
      <Modal
        show={!!deleteConfirm}
        onHide={() => setDeleteConfirm(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmer la suppression</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Êtes-vous sûr de vouloir supprimer le modèle{' '}
            <strong>"{deleteConfirm?.name}"</strong> ?
          </p>
          <Alert variant="info" className="mb-0">
            <small>
              Cette action est un <strong>soft delete</strong> : le modèle sera
              masqué mais les commandes historiques qui l'utilisent resteront intactes.
            </small>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setDeleteConfirm(null)}
            disabled={deleting}
          >
            Annuler
          </Button>
          <Button
            variant="danger"
            onClick={confirmDelete}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Suppression...
              </>
            ) : (
              'Supprimer'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Container>
  );
};

export default ModelsManagement;